import { SonioxClient } from '@soniox/client';
import ApplicationController from './application_controller';

// Voice dictation for custom form text controls (text area, text input, WYSIWYG). Rendered by
// shared/form/controls/_voice_dictation.html.erb, gated by :custom_form_system_voice_to_text_enabled.
//
// The mic button starts a Soniox real-time transcription session. Audio streams from the user's
// microphone to Soniox over a websocket authenticated with a short-lived transcribe-only key
// minted by our server (POST /voice-dictation/temporary-api-key) — the permanent API key never
// reaches the browser.
//
// Insertion behavior (both field types): finalized text is inserted at the caret, and the
// still-recognizing (non-final) tail is shown live and replaced as Soniox refines it. In plain
// fields the tail is spliced into the value; in CKEditor it renders via an editing-view-only
// marker element so it never touches the saved document or undo history.
//
// ─── READ THIS before touching startup timing: the click-to-talk delay is NOT fixable ───
//
// There is an unavoidable delay between clicking the mic and the moment speech is actually
// captured. It is physics and platform behavior, not slow code:
//
//   1. getUserMedia resolving does NOT mean audio is flowing. The OS input path and the
//      browser's audio processing (echo cancellation, noise suppression, gain) take roughly
//      0.5–3 seconds to spin up and converge. During that window the stream delivers silence
//      or heavily suppressed audio. Words spoken then ARE NOT IN THE AUDIO — no buffering,
//      readiness signal, or server-side trick can recover sound that was never captured.
//      Bluetooth headsets are the worst case (audio profile switching alone can take 2–3s).
//   2. Failed approaches, so nobody retries them: (a) turning red on the first non-zero
//      sample — mics emit driver noise-floor immediately while the speech path is still
//      muted, so red lied; (b) turning red when the server first reports decoded audio
//      (total_audio_proc_ms > 0) alone — decoded SILENCE counts, so red still lied.
//   3. What we actually do: hovering the button pre-acquires the mic so spin-up happens
//      before the click (warm mic lifetime is pointer-driven — see warmUpMic/coolWarmMic),
//      and the button turns red only when the server has confirmed decoded audio AND the
//      stream has been alive for at least MIC_READY_MS. Hover time counts toward that floor,
//      so hover-then-click pays little or none of it; a cold instant click waits it out.
//      Verified empirically: hover length before clicking directly improves capture quality.
//   4. Consequently: red = "talk now", grey = "wait". Making red arrive sooner does not make
//      the system faster — it just silently drops the user's first words. If startup feels
//      slow, the only honest levers are more prewarming or clearer UX, never an earlier red.
// Only one dictation session may run at a time across the page. Starting a new one
// gracefully stops the previous, so its finalized text still lands in its field.
let activeDictation = null;

// Marker naming the position of the live (still-recognizing) preview inside a WYSIWYG
// editor. It downcasts to an editing-view-only element (see registerEditorPreview), so the
// preview text is never part of the document model: saves, autosave snapshots, and undo
// history only ever contain finalized text.
const PREVIEW_MARKER = 'voiceDictationPreview';

// Temporary key cache, shared across all dictation fields on the page. Minting a key costs a
// server round trip (~500ms) that would otherwise sit inside the session start sequence, so we
// prefetch on hover and reuse the key until shortly before it expires (keys allow ~60s to open
// a stream). A single in-flight fetch is shared by concurrent callers.
let cachedKey = null;
let inflightKeyFetch = null;

// Warm microphone stream, shared page-wide. Microphone hardware delivers silence for up to a
// few seconds after acquisition while the input path spins up (Bluetooth mics are worst), so a
// mic acquired at click time loses the first spoken words. Hovering the mic button starts the
// spin-up early; the warm stream is handed to the session on click, or released after a short
// hold if no click follows. While warm, the browser's recording indicator is visible — that is
// inherent to holding a live stream.
// Warm-mic lifetime is pointer-driven, not a flat timer: hovering longer measurably improves
// capture (the mic keeps settling), so warmth must never expire mid-hover. The stream is held
// while the pointer is over the button, released shortly after it leaves, with an absolute
// cap as a safety net against a missed pointerleave.
const WARM_MIC_LEAVE_MS = 3000;
const WARM_MIC_MAX_MS = 60000;
// Minimum time a mic stream must have been alive before the button may turn red. The
// browser's audio processing (echo cancellation, noise suppression) suppresses early speech
// to near-silence while it converges, so the server can confirm decoded audio that contained
// no hearable words. Hover warmth counts toward this floor: a natural hover-then-click pays
// little or none of it. Deliberately generous (was 1200): fast talkers start speaking the
// instant the button turns red, so red must not arrive until even a slow-settling mic
// (Bluetooth profile switching) is genuinely capturing.
const MIC_READY_MS = 2200;
const MIC_CONSTRAINTS = { audio: { echoCancellation: true, noiseSuppression: true } };
// Soniox bills every second the stream is open, silence included, so a session must not outlive
// the person using it. Two limits, both set per mount through Stimulus values (the field wrapper
// and the AI Scribe panel pick their own) and both ending in the graceful stop that keeps every
// finalized word:
//   - idleStopMs: no speech for this long ends the session. Every word the server hears resets
//     the clock, so someone actually dictating never reaches it; a mic left open on an empty
//     room does.
//   - maxSessionMs: a ceiling for one click, which also covers background noise or a TV that
//     keeps producing words. Once the session is within SESSION_WRAP_UP_MS of the ceiling it
//     stops at the next pause in speech (the server's <end> token), so a sentence is not cut in
//     half; at the ceiling it stops regardless. The clocks live here, not on the server: the
//     temporary key caps one server session (max_session_duration_seconds in
//     Soniox::TemporaryAPIKey), but auto_reconnect can open a fresh session after a drop, so
//     only this ceiling bounds what one click can stream. The field default stays under the key
//     cap so a field session ends on our graceful stop, never on a server cut.
// 0 disables a limit.
const DEFAULT_IDLE_STOP_MS = 3 * 60 * 1000;
const DEFAULT_MAX_SESSION_MS = 55 * 60 * 1000;
const SESSION_WRAP_UP_MS = 5 * 60 * 1000;
let warmMic = null;
let warmMicPromise = null;

// getUserMedia — and navigator.mediaDevices itself — exist only in a secure context: HTTPS,
// or http://localhost / http://127.0.0.1. Loaded over plain HTTP from a LAN IP or machine
// hostname (e.g. a PC hitting a dev server running on another computer), the browser leaves
// navigator.mediaDevices undefined and mic capture is impossible there. Every mic entry point
// funnels through this so an unsupported context degrades gracefully (connect() mutes the
// button and clicking it explains why) instead of throwing "Cannot read properties of
// undefined (reading 'getUserMedia')".
function micSupported() {
  return !!navigator.mediaDevices?.getUserMedia;
}

function warmUpMic() {
  if (!micSupported()) return;
  if (warmMic) {
    // Pointer came back — cancel any pending release.
    clearTimeout(warmMic.leaveTimer);
    warmMic.leaveTimer = null;
    return;
  }
  if (warmMicPromise) return;
  warmMicPromise = navigator.mediaDevices
    .getUserMedia(MIC_CONSTRAINTS)
    .then((stream) => {
      warmMic = {
        stream,
        acquiredAt: Date.now(),
        leaveTimer: null,
        capTimer: setTimeout(releaseWarmMic, WARM_MIC_MAX_MS),
      };
    })
    .catch(() => {
      // Ignore here — the click path will surface a real permission error.
    })
    .finally(() => {
      warmMicPromise = null;
    });
}

function coolWarmMic() {
  if (!warmMic || warmMic.leaveTimer) return;
  warmMic.leaveTimer = setTimeout(releaseWarmMic, WARM_MIC_LEAVE_MS);
}

function takeWarmMic() {
  if (!warmMic) return null;
  clearTimeout(warmMic.leaveTimer);
  clearTimeout(warmMic.capTimer);
  const { stream, acquiredAt } = warmMic;
  warmMic = null;
  return stream.active ? { stream, acquiredAt } : null;
}

// The warm mic if one is ready or in flight (a fast hover-then-click must not race past the
// hover's acquisition and open a second one), otherwise a fresh acquisition. Returns the
// stream plus when it came alive, for the MIC_READY_MS floor. Throws on denial.
async function obtainMic() {
  if (warmMicPromise) await warmMicPromise;
  const warm = takeWarmMic();
  if (warm) return warm;
  const stream = await navigator.mediaDevices.getUserMedia(MIC_CONSTRAINTS);
  return { stream, acquiredAt: Date.now() };
}

function releaseWarmMic() {
  if (!warmMic) return;
  clearTimeout(warmMic.leaveTimer);
  clearTimeout(warmMic.capTimer);
  warmMic.stream.getTracks().forEach((track) => track.stop());
  warmMic = null;
}

// Never carry a live mic across a Turbo navigation.
document.addEventListener('turbo:before-cache', releaseWarmMic);

function fetchDictationKey() {
  if (cachedKey && Date.now() < cachedKey.reuseUntil) return Promise.resolve(cachedKey.key);
  if (inflightKeyFetch) return inflightKeyFetch;
  inflightKeyFetch = fetch('/voice-dictation/temporary-api-key', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || '',
      Accept: 'application/json',
    },
  })
    .then(async (response) => {
      if (!response.ok) throw new Error(`Temporary key request failed (${response.status})`);
      const data = await response.json();
      // Reuse until 10s before the server-reported expiry (fallback: 45s from now).
      const expiresAt = data.expires_at ? Date.parse(data.expires_at) : Date.now() + 55000;
      cachedKey = { key: data.api_key, reuseUntil: expiresAt - 10000 };
      return data.api_key;
    })
    .finally(() => {
      inflightKeyFetch = null;
    });
  return inflightKeyFetch;
}

// The SDK client is a lazy page-wide singleton. Its async `config` callback runs once per
// recording session AND once per auto-reconnect attempt, each time flowing through
// fetchDictationKey's cache — so a reconnect mid-session reuses the cached temporary key when
// it's still fresh, or mints a new one when it isn't.
let sonioxClient = null;
function getSonioxClient() {
  if (!sonioxClient) {
    sonioxClient = new SonioxClient({
      config: async () => ({ api_key: await fetchDictationKey() }),
    });
  }
  return sonioxClient;
}

// How often the MediaRecorder emits an encoded chunk (matches the SDK's own MicrophoneSource).
const RECORDER_TIMESLICE_MS = 60;

// Adapts our hover-warmed MediaStream (see warmUpMic) to the SDK's AudioSource interface — the
// documented extension point for custom capture (a duck-typed object with start/stop and
// optional pause/resume/restart). The SDK's built-in MicrophoneSource always acquires its own
// stream via getUserMedia inside start(), which would throw away the warm-up: the entire point
// of the hover flow is that the mic's spin-up happened before the click. So we hand the SDK an
// already-live stream and only manage the MediaRecorder here, mirroring MicrophoneSource's
// encoding behavior (default container format, same timeslice) — including restart(), which
// auto-reconnect calls so a new server session gets a fresh container header (a mid-stream
// chunk of the old recording would be undecodable).
function warmStreamSource(stream) {
  let recorder = null;
  let onData = null;
  let onError = null;

  const startRecorder = () => {
    recorder = new MediaRecorder(stream);
    recorder.addEventListener('dataavailable', onData);
    recorder.addEventListener('error', onError);
    recorder.start(RECORDER_TIMESLICE_MS);
  };

  return {
    async start(handlers) {
      if (typeof MediaRecorder === 'undefined') throw new Error('MediaRecorder is not available');
      if (!stream?.active) throw new Error('Microphone stream is no longer active');
      onData = (event) => {
        if (event.data.size === 0) return;
        event.data.arrayBuffer().then(
          (buffer) => handlers.onData(buffer),
          (error) => handlers.onError(error instanceof Error ? error : new Error(String(error)))
        );
      };
      onError = (event) => handlers.onError(new Error(event.message || 'MediaRecorder error'));
      startRecorder();
    },

    stop() {
      if (recorder) {
        const current = recorder;
        const dataHandler = onData;
        const errorHandler = onError;
        if (current.state !== 'inactive') {
          // Keep listeners attached until the recorder actually stops, so the final flush chunk
          // (the tail of the user's last words) still reaches the server.
          current.addEventListener(
            'stop',
            () => {
              current.removeEventListener('dataavailable', dataHandler);
              current.removeEventListener('error', errorHandler);
            },
            { once: true }
          );
          current.stop();
        } else {
          current.removeEventListener('dataavailable', dataHandler);
          current.removeEventListener('error', errorHandler);
        }
        recorder = null;
      }
      // The AudioSource contract is "release all resources". The controller's releaseMicStream
      // is an idempotent second stop covering sessions abandoned before the recording existed.
      stream?.getTracks().forEach((track) => track.stop());
    },

    pause() {
      if (recorder?.state === 'recording') recorder.pause();
    },

    resume() {
      if (recorder?.state === 'paused') recorder.resume();
    },

    restart() {
      if (!recorder) return;
      // Listeners come off the old recorder before stopping it: its flush chunk belongs to the
      // dead server session and must not leak into the new one.
      recorder.removeEventListener('dataavailable', onData);
      recorder.removeEventListener('error', onError);
      if (recorder.state !== 'inactive') recorder.stop();
      startRecorder();
    },
  };
}

// Explain, in a centered modal, why dictation can't run. A corner toast would be missed on a
// long form scrolled far from the field, so the message must be in view regardless of scroll
// position. Built on demand and torn down on close (like showToast) so a page with many
// dictation fields never carries N modals. Bootstrap 4 modal via jQuery is the app's standard
// (see the other .modal('show') call sites); a minimal manual fallback covers jQuery being
// unexpectedly absent. Text is set with textContent, never innerHTML — the copy is all ours,
// but stay injection-safe by construction.
function showDictationDialog(title, message, dismissLabel, detail) {
  document.getElementById('voice-dictation-dialog')?.remove();

  const modal = document.createElement('div');
  modal.id = 'voice-dictation-dialog';
  modal.className = 'modal fade';
  modal.tabIndex = -1;
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-labelledby', 'voice-dictation-dialog-title');
  modal.innerHTML = `
    <div class="modal-dialog modal-dialog-centered" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="voice-dictation-dialog-title"></h5>
          <button type="button" class="close" data-dismiss="modal" aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="modal-body">
          <p class="mb-0"></p>
          <p class="text-muted small mb-0 mt-2" data-detail hidden></p>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-primary" data-dismiss="modal"></button>
        </div>
      </div>
    </div>`;
  modal.querySelector('.modal-title').textContent = title;
  modal.querySelector('.modal-body p').textContent = message;
  if (detail) {
    const detailEl = modal.querySelector('[data-detail]');
    detailEl.textContent = detail;
    detailEl.hidden = false;
  }
  modal.querySelector('.modal-footer .btn').textContent = dismissLabel;
  document.body.appendChild(modal);

  if (window.$?.fn?.modal) {
    const $modal = window.$(modal);
    $modal.modal('show');
    $modal.on('hidden.bs.modal', () => {
      $modal.modal('dispose');
      modal.remove();
    });
    return;
  }

  // Fallback: no Bootstrap modal plugin — show manually with a backdrop and wire up dismissal.
  modal.classList.add('show');
  modal.style.display = 'block';
  document.body.classList.add('modal-open');
  const backdrop = document.createElement('div');
  backdrop.id = 'voice-dictation-dialog-backdrop';
  backdrop.className = 'modal-backdrop fade show';
  document.body.appendChild(backdrop);
  const close = () => {
    modal.remove();
    backdrop.remove();
    document.body.classList.remove('modal-open');
  };
  modal.querySelectorAll('[data-dismiss="modal"]').forEach((el) => el.addEventListener('click', close));
  backdrop.addEventListener('click', close);
}

// A stray error dialog must not survive into the Turbo page cache.
document.addEventListener('turbo:before-cache', () => {
  document.getElementById('voice-dictation-dialog')?.remove();
  document.getElementById('voice-dictation-dialog-backdrop')?.remove();
});

export default class extends ApplicationController {
  static targets = ['button', 'icon'];
  static values = {
    inputId: String,
    labels: Object,
    idleStopMs: { type: Number, default: DEFAULT_IDLE_STOP_MS },
    maxSessionMs: { type: Number, default: DEFAULT_MAX_SESSION_MS },
  };

  connect() {
    this.recording = null;
    this.editor = null;
    this.starting = false;
    this.frozen = false;
    this.micStream = null;
    this.micReadyAt = 0;
    this.startTimeout = null;
    this.readyTimer = null;
    this.blockedTimer = null;
    this.idleTimer = null;
    this.wrapUpTimer = null;
    this.maxSessionTimer = null;
    this.wrappingUp = false;
    this.limitReason = null;
    this.escHandler = null;
    this.typingHandler = null;
    this.outsideClickHandler = null;
    // Dictation may be structurally impossible in this context (unsupported browser, or an
    // insecure/non-https connection where navigator.mediaDevices is absent). Rather than hide
    // the button — the silent failure that leaves people, and support, guessing — keep it
    // visible but muted; clicking it opens a modal that names the exact reason.
    this.unavailableReason = this.detectUnavailableReason();
    if (this.unavailableReason) this.markUnavailable();
  }

  disconnect() {
    clearTimeout(this.blockedTimer);
    clearTimeout(this.startTimeout);
    clearTimeout(this.readyTimer);
    this.readyTimer = null;
    this.clearSessionLimits();
    this.removeEscapeHandler();
    this.removeTypingHandler();
    this.removeOutsideClickHandler();
    this.clearEditorPreview();
    this.releaseMicStream();
    this.recording?.cancel();
    this.recording = null;
    if (activeDictation === this) activeDictation = null;
  }

  toggle() {
    if (this.unavailableReason) {
      this.explainUnavailable();
      return;
    }
    if (this.starting) return;
    if (this.recording) {
      this.stopDictation();
    } else {
      this.startDictation();
    }
  }

  // The keyboard path to dictation: Alt+Shift+M from the wrapped field starts or stops it, the
  // mic button being out of the Tab order (Tab goes field to field, Jason 2026-10-13; a
  // keyboard-only user still needs a way in, Kevin 2026-10-17). Bound as a keydown action on
  // the wrapper, so it fires only while the caret is in this field or its editor. The code,
  // not the key: with Option held, macOS types "µ" for the M key.
  shortcut(event) {
    if (!event.altKey || !event.shiftKey || event.ctrlKey || event.metaKey || event.code !== 'KeyM') return;
    event.preventDefault();
    event.stopPropagation();
    this.toggle();
  }

  // Toolbar-button trick: swallow the button's mousedown so clicking the mic never steals
  // focus from the field — the caret stays exactly where the user left it.
  keepFocus(event) {
    event.preventDefault();
  }

  // Warm both slow pieces (temporary key and microphone) while the pointer is still on its
  // way to a click. Failures are ignored here; startDictation retries and surfaces real errors.
  prewarm() {
    if (this.unavailableReason) return;
    fetchDictationKey().catch(() => {});
    if (!this.recording && !this.starting) warmUpMic();
  }

  // Pointer left without a click — release the warm mic shortly so the browser's recording
  // indicator doesn't linger. No-op if the stream was already handed to a session.
  cooldown() {
    coolWarmMic();
  }

  // Stop or abandon the session regardless of phase: a running client stops gracefully
  // (finalized text kept), and a session still acquiring the mic is abandoned via the
  // post-acquisition guard in startDictation.
  haltSession() {
    if (this.recording) {
      this.stopDictation();
    } else if (activeDictation === this) {
      activeDictation = null;
    }
  }

  // While a session is live (or starting), Escape stops the mic — and is swallowed in the
  // capture phase so it doesn't also close trays, modals, or CKEditor balloons: the user's
  // intent is "stop dictating", not "close things". Installed per-session on the document
  // (focus may be anywhere), removed on every session exit path.
  installEscapeHandler() {
    this.escHandler = (event) => {
      if (event.key !== 'Escape') return;
      if (!this.recording && !this.starting) return;
      event.preventDefault();
      event.stopPropagation();
      this.haltSession();
    };
    document.addEventListener('keydown', this.escHandler, { capture: true });
  }

  // Typing into a plain field mid-dictation would otherwise be overwritten by the next
  // render (renderTextarea rebuilds the value from the session-start snapshot). The user
  // choosing to type means dictation is over: freeze the field exactly as they see it and
  // stop the session. beforeinput fires only for user edits, never for our programmatic
  // value updates, so there is no self-trigger.
  installTypingHandler() {
    if (this.editor) return;
    this.typingHandler = () => {
      this.frozen = true;
      this.haltSession();
    };
    this.input.addEventListener('beforeinput', this.typingHandler);
  }

  removeTypingHandler() {
    if (!this.typingHandler) return;
    this.input?.removeEventListener('beforeinput', this.typingHandler);
    this.typingHandler = null;
  }

  // Clicking outside the control being dictated into ends the session: the user has moved
  // on, and speech must not keep streaming into a field they've left. "Inside" is the whole
  // custom-form control (the closest .form-group), not just our wrapper — controls like
  // checkboxes-with-text, prognosis, and therapy recommendations put checkboxes and selects
  // beside the dictation field, and clicking those is still working within the same control.
  // CKEditor renders balloons and panels into a body-attached .ck-body-wrapper, so clicks
  // there count as inside too. pointerdown in the capture phase: it fires before focus moves
  // or a widget swallows the event, and unlike click it can't be lost to a press-drag-release
  // that starts and ends on different elements. Installed per-session, removed on every exit.
  installOutsideClickHandler() {
    this.outsideClickHandler = (event) => {
      if (!this.recording && !this.starting) return;
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      const boundary = this.element.closest('.form-group') || this.element;
      if (boundary.contains(target)) return;
      if (target.closest('.ck-body-wrapper')) return;
      this.haltSession();
    };
    document.addEventListener('pointerdown', this.outsideClickHandler, { capture: true });
  }

  removeOutsideClickHandler() {
    if (!this.outsideClickHandler) return;
    document.removeEventListener('pointerdown', this.outsideClickHandler, { capture: true });
    this.outsideClickHandler = null;
  }

  // Server-provided i18n labels (see the wrapper partial); English fallbacks only matter if
  // the data attribute is somehow absent.
  label(key) {
    return (
      this.labelsValue?.[key] ||
      {
        dictate: 'Dictate into this field (Alt+Shift+M)',
        connecting: 'Connecting…',
        stop: 'Stop dictation',
        blocked: 'Microphone blocked — check your browser permissions',
        could_not_start: 'Dictation could not start — check your connection and try again',
        unavailable_retry: 'Dictation is unavailable right now — please try again',
        unavailable_hint: "Voice dictation isn't available here — click for details",
        unavailable_heading: 'Voice dictation unavailable',
        dismiss: 'Got it',
        error_insecure: 'Voice dictation needs a secure connection to use your microphone.',
        error_unsupported: "Voice dictation can't run in this browser.",
        error_permission:
          "Ambiki doesn't have permission to use your microphone. Allow microphone access in your browser settings, then try again.",
        error_nodevice: 'No microphone was found. Please connect a microphone and try again.',
        error_inuse: 'Your microphone is being used by another app. Close it, then try again.',
        idle_stopped: 'Dictation stopped because no one was talking. Click the mic to keep going.',
        session_limit_stopped: 'Dictation stopped after a long session. Click the mic to keep going.',
      }[key]
    );
  }

  removeEscapeHandler() {
    if (!this.escHandler) return;
    document.removeEventListener('keydown', this.escHandler, { capture: true });
    this.escHandler = null;
  }

  // Session-start timeline (+Nms since click), dormant in normal use — uncomment the
  // console.info to revive it. When a user reports "it cut off my first words", one pasted
  // console block answers where the time went (cold vs warm mic, which input device, server
  // confirm, red) instead of another round of guessing. Keep console.info, not console.debug:
  // Chrome hides debug behind the Verbose level, and these lines exist precisely for the
  // moment someone confused is looking at the console.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  debugLog(message) {
    // const elapsed = this.sessionT0 ? Date.now() - this.sessionT0 : 0;
    // console.info(`voice-dictation +${elapsed}ms: ${message}`); // eslint-disable-line no-console
  }

  async startDictation() {
    // Prefer the explicit id; fall back to the field inside our wrapper for controls whose
    // inputs have no id (e.g. goal status comments). :not(.hidden) skips CKEditor's sync textarea.
    this.input =
      (this.inputIdValue && document.getElementById(this.inputIdValue)) ||
      this.element.querySelector('textarea:not(.hidden), input[type="text"]');
    if (!this.input) return;

    if (activeDictation && activeDictation !== this) activeDictation.stopDictation();
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    activeDictation = this;

    // A new session invalidates any lingering blocked flash — its delayed resetUi would
    // otherwise fire mid-session and wipe the connecting/recording state.
    clearTimeout(this.blockedTimer);
    this.buttonTarget.classList.remove('voice-dictation__button--blocked');
    this.iconTarget.className = 'bi bi-mic';

    this.starting = true;
    this.frozen = false;
    this.sessionT0 = Date.now();
    this.firstTextLogged = false;
    this.debugLog('click — session starting');
    this.installEscapeHandler();
    this.installOutsideClickHandler();
    this.prepareInsertPoint();
    this.installTypingHandler();
    this.showConnecting();

    // Start the key fetch immediately so it runs concurrently with mic activation — record()'s
    // config callback then hits fetchDictationKey's cache (or joins this in-flight fetch)
    // instead of paying the mint round trip inside the session start sequence.
    fetchDictationKey().catch(() => {});

    // Take the hover-warmed mic (spin-up already done — capture starts at the first word),
    // awaiting an in-flight warm acquisition rather than racing it. Falls back to a fresh
    // acquisition when there was no hover (keyboard, touch).
    try {
      const { stream, acquiredAt } = await obtainMic();
      this.micStream = stream;
      this.micReadyAt = acquiredAt + MIC_READY_MS;
      const aliveMs = Date.now() - acquiredAt;
      const device = stream.getAudioTracks()[0]?.label || 'unknown device';
      this.debugLog(
        `mic obtained [${device}] (${aliveMs > 100 ? `warm, alive ${aliveMs}ms` : 'COLD'}); floor allows red in ${Math.max(0, this.micReadyAt - Date.now())}ms`
      );
    } catch (error) {
      this.starting = false;
      this.removeEscapeHandler();
      this.removeTypingHandler();
      this.removeOutsideClickHandler();
      if (activeDictation === this) activeDictation = null;
      this.resetUi();
      this.showDictationError(this.classifyMediaError(error));
      return;
    }

    // Another field may have taken over, Escape was pressed, typing began, or the page
    // navigated while the mic was being acquired — abandon this session instead of opening
    // a second one.
    if (activeDictation !== this) {
      this.releaseMicStream();
      this.starting = false;
      this.removeEscapeHandler();
      this.removeTypingHandler();
      this.removeOutsideClickHandler();
      this.resetUi();
      return;
    }

    // record() returns synchronously — the async work (key fetch, websocket connect) starts on
    // the next microtask, so every listener below is attached before anything can fire. Audio
    // flows through warmStreamSource so the server hears the hover-warmed stream whose spin-up
    // time is already paid. auto_reconnect re-runs the client's config callback per attempt, so
    // a dropped websocket resumes with a valid temporary key and our source's restart() gives
    // the new server session a fresh container header.
    this.recording = getSonioxClient().realtime.record({
      model: 'stt-rt-v5',
      language_hints: ['en'],
      enable_endpoint_detection: true,
      source: warmStreamSource(this.micStream),
      auto_reconnect: true,
    });
    this.recording.on('result', (result) => this.handlePartialResult(result));
    this.recording.on('finished', () => this.finishSession());
    this.recording.on('error', (error) => this.handleError(error));
    // `starting` stays true until showRecording(): the button ignores clicks for the whole
    // grey connecting phase, so double clicks and premature "unclicks" are impossible. Red is
    // gated on the server confirming decoded audio (see handlePartialResult); if that proof
    // never arrives, fail visibly instead of leaving the button grey forever.
    this.startTimeout = setTimeout(() => {
      if (!this.starting) return;
      this.recording?.cancel();
      this.finishSession();
      this.flashBlocked(this.label('could_not_start'));
    }, 8000);
  }

  stopDictation() {
    if (!this.recording) return;
    if (this.recording.state === 'recording') {
      // Graceful stop: flushes the remaining audio and waits for final tokens; 'finished'
      // fires when the server acknowledges, and finishSession runs there. If the session dies
      // mid-flush, stop() rejects — the 'error' event handler already covers that path, so
      // the rejection itself needs no second handler.
      this.recording.stop().catch(() => {});
    } else {
      // Not streaming yet (still connecting) or already dying — nothing worth flushing.
      this.recording.cancel();
      this.finishSession();
    }
  }

  // Stop our own mic tracks. warmStreamSource's stop() also stops them once a session owns the
  // stream, but sessions abandoned before record() runs (mic denied mid-acquire, field takeover,
  // Escape during acquisition) only have this — and leaving the tracks live keeps the browser's
  // recording indicator on after the session ends.
  releaseMicStream() {
    this.micStream?.getTracks().forEach((track) => track.stop());
    this.micStream = null;
  }

  // Capture where dictated text should go before the session opens, and put the blinking
  // caret there so the user sees exactly where their words will land.
  prepareInsertPoint() {
    this.editor = this.element.querySelector('.wysiwyg-ckeditor')?.ckeditorInstance || null;
    if (this.editor) {
      this.firstEditorInsert = true;
      this.registerEditorPreview();
      // If the editor doesn't hold the caret, park it at the end of the document.
      if (!this.editor.editing.view.document.isFocused) {
        this.editor.model.change((writer) => {
          writer.setSelection(writer.createPositionAt(this.editor.model.document.getRoot(), 'end'));
        });
      }
      this.editor.editing.view.focus();
    } else {
      // The mic button never steals focus (see keepFocus), so if the field held the caret it
      // still does. Otherwise focus it and start the caret at the end of any existing text.
      if (document.activeElement !== this.input) {
        this.input.focus();
        const end = this.input.value.length;
        this.input.setSelectionRange?.(end, end);
      }
      const caret = this.input.selectionStart ?? this.input.value.length;
      this.beforeText = this.input.value.slice(0, caret);
      this.afterText = this.input.value.slice(caret);
      this.finalText = '';
      // Separate dictated text from existing text with a space when needed.
      this.leadingSpace = this.beforeText && !/\s$/.test(this.beforeText) ? ' ' : '';
    }
  }

  handlePartialResult(result) {
    // The readiness signal is two-part: the server reporting decoded audio proves the
    // transport pipeline (mic → encoder → websocket → Soniox) on any connection speed, and
    // the MIC_READY_MS floor covers the browser's audio processing warm-up, during which
    // decoded audio exists but early speech is suppressed to silence. Red needs both.
    if (this.starting && (result.total_audio_proc_ms || 0) > 0 && !this.readyTimer) {
      this.debugLog(`server confirmed decoded audio (${result.total_audio_proc_ms}ms processed)`);
      const wait = Math.max(0, this.micReadyAt - Date.now());
      if (wait === 0) {
        this.showRecording();
      } else {
        this.readyTimer = setTimeout(() => {
          this.readyTimer = null;
          if (this.starting) this.showRecording();
        }, wait);
      }
    }
    if (!this.firstTextLogged && result.tokens?.some((t) => t.text && t.text !== '<end>' && t.text !== '<fin>')) {
      this.firstTextLogged = true;
      this.debugLog(
        `first transcribed text arrived: "${result.tokens
          .map((t) => t.text)
          .join('')
          .slice(0, 40)}"`
      );
    }

    const tokens = result.tokens || [];
    // Any recognized speech, provisional or final, proves someone is still talking.
    if (tokens.some((t) => t.text && t.text !== '<end>' && t.text !== '<fin>' && t.text.trim())) {
      this.armIdleTimer();
    }

    let finalChunk = '';
    let nonFinalText = '';
    tokens.forEach((token) => {
      // <end> and <fin> are control tokens from endpoint detection / finalization, not speech.
      if (token.text === '<end>' || token.text === '<fin>') return;
      if (token.is_final) {
        finalChunk += token.text;
      } else {
        nonFinalText += token.text;
      }
    });

    if (this.editor) {
      this.updateEditorPreview(finalChunk, nonFinalText);
    } else {
      this.finalText += finalChunk;
      // Every message carries the full refreshed non-final tail, so render replaces the preview.
      this.renderTextarea(nonFinalText);
    }

    // Near the ceiling, the first pause in speech ends the session. Checked after the text above
    // is applied, so the words that came with the <end> token are kept.
    if (this.wrappingUp && tokens.some((t) => t.text === '<end>')) this.stopForLimit('session_limit_stopped');
  }

  // --- Session limits (see DEFAULT_IDLE_STOP_MS) ---

  // Armed when the button turns red: capture has really started, so that is when billing
  // matters. Reconnects keep the same clocks, so the ceiling holds across them.
  startSessionLimits() {
    this.clearSessionLimits();
    this.armIdleTimer();
    const max = this.maxSessionMsValue;
    if (max > 0) {
      this.wrapUpTimer = setTimeout(
        () => {
          this.wrapUpTimer = null;
          this.wrappingUp = true;
        },
        Math.max(0, max - SESSION_WRAP_UP_MS)
      );
      this.maxSessionTimer = setTimeout(() => {
        this.maxSessionTimer = null;
        this.stopForLimit('session_limit_stopped');
      }, max);
    }
  }

  armIdleTimer() {
    clearTimeout(this.idleTimer);
    this.idleTimer = null;
    if (!this.recording || this.idleStopMsValue <= 0) return;
    this.idleTimer = setTimeout(() => {
      this.idleTimer = null;
      this.stopForLimit('idle_stopped');
    }, this.idleStopMsValue);
  }

  clearSessionLimits() {
    clearTimeout(this.idleTimer);
    clearTimeout(this.wrapUpTimer);
    clearTimeout(this.maxSessionTimer);
    this.idleTimer = null;
    this.wrapUpTimer = null;
    this.maxSessionTimer = null;
    this.wrappingUp = false;
  }

  // Ends the session through the same graceful stop as the user's own click (the remaining
  // audio is flushed and its final words land), then says why once the session has closed.
  stopForLimit(reason) {
    if (!this.recording || this.limitReason) return;
    this.limitReason = reason;
    this.clearSessionLimits();
    this.haltSession();
  }

  // Mirror the textarea feel in CKEditor: finalized text is inserted for real, while the
  // still-recognizing tail renders as a grey provisional element at the caret. Every message
  // carries the full refreshed tail, so each update replaces the previous preview.
  //
  // The preview is cosmetic and must NEVER interfere with real text: finals insert first,
  // unconditionally, and any exception in the preview machinery disables the preview for the
  // rest of the session instead of eating the message (and its finalized tokens) with it.
  updateEditorPreview(finalChunk, nonFinalText) {
    if (finalChunk) this.insertIntoEditor(finalChunk);
    if (this.previewBroken) return;
    try {
      const { model } = this.editor;
      model.enqueueChange({ isUndoable: false }, (writer) => {
        if (model.markers.has(PREVIEW_MARKER)) writer.removeMarker(PREVIEW_MARKER);
        if (nonFinalText) {
          const position = model.document.selection.getFirstPosition();
          let display = nonFinalText;
          if (position && !position.isAtStart && !/^\s/.test(display)) display = ` ${display}`;
          this.editor.voiceDictationPreviewText = display;
          writer.addMarker(PREVIEW_MARKER, {
            range: writer.createRange(position),
            usingOperation: false,
            affectsData: false,
          });
        }
      });
    } catch (error) {
      this.previewBroken = true;
      console.warn('Voice dictation: live preview disabled for this session', error); // eslint-disable-line no-console
    }
  }

  // One-time (per editor instance) setup of the preview rendering: the collapsed marker
  // downcasts to a UI element in the editing view only. Recreating the marker on each update
  // re-runs this render with the fresh preview text.
  registerEditorPreview() {
    const { editor } = this;
    this.previewBroken = false;
    if (editor.voiceDictationPreviewReady) return;
    try {
      editor.conversion.for('editingDowncast').markerToElement({
        model: PREVIEW_MARKER,
        view: (data, { writer }) =>
          // eslint-disable-next-line func-names
          writer.createUIElement('span', { class: 'voice-dictation-preview' }, function (domDocument) {
            const domElement = this.toDomElement(domDocument);
            domElement.textContent = editor.voiceDictationPreviewText || '';
            return domElement;
          }),
      });
      editor.voiceDictationPreviewReady = true;
    } catch (error) {
      this.previewBroken = true;
      console.warn('Voice dictation: live preview unavailable for this editor', error); // eslint-disable-line no-console
    }
  }

  clearEditorPreview() {
    if (!this.editor) return;
    try {
      const { model } = this.editor;
      this.editor.voiceDictationPreviewText = '';
      if (model.markers.has(PREVIEW_MARKER)) {
        model.enqueueChange({ isUndoable: false }, (writer) => writer.removeMarker(PREVIEW_MARKER));
      }
    } catch {
      // Session teardown must never fail on preview cleanup.
    }
  }

  renderTextarea(preview) {
    // Frozen means the user started typing mid-dictation: the field is theirs now, and no
    // further render may overwrite what they see (including the session's final flush).
    if (this.frozen) return;
    const dictated = this.finalText + preview;
    const insertion = dictated ? this.leadingSpace + dictated : '';
    this.input.value = this.beforeText + insertion + this.afterText;
    const caret = (this.beforeText + insertion).length;
    this.input.setSelectionRange?.(caret, caret);
    // Let autosize/auto-save listeners react to the programmatic change.
    this.input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  insertIntoEditor(text) {
    let chunk = text;
    if (this.firstEditorInsert) {
      this.firstEditorInsert = false;
      const position = this.editor.model.document.selection.getFirstPosition();
      if (position && !position.isAtStart && !/^\s/.test(chunk)) chunk = ` ${chunk}`;
    }
    this.editor.model.change((writer) => {
      this.editor.model.insertContent(writer.createText(chunk));
    });
  }

  finishSession() {
    clearTimeout(this.startTimeout);
    this.startTimeout = null;
    clearTimeout(this.readyTimer);
    this.readyTimer = null;
    this.clearSessionLimits();
    const { limitReason } = this;
    this.limitReason = null;
    this.removeEscapeHandler();
    this.removeTypingHandler();
    this.removeOutsideClickHandler();
    this.releaseMicStream();
    this.clearEditorPreview();
    if (this.input && !this.editor) this.renderTextarea('');
    this.recording = null;
    this.editor = null;
    this.starting = false;
    if (activeDictation === this) activeDictation = null;
    this.resetUi();
    if (limitReason) this.flashBlocked(this.label(limitReason));
    // Tell the surrounding UI the session is over, whatever ended it (the user's click, Escape,
    // typing, an error, or one of the limits above), so a label such as the AI Scribe's
    // "Listening..." follows the mic instead of outliving it. reason is the limit key or null.
    this.dispatch('stopped', { detail: { reason: limitReason } });
  }

  handleError(error) {
    console.error(`Voice dictation error: ${error?.message || error}`); // eslint-disable-line no-console
    this.finishSession();
    // With the stream pre-acquired by obtainMic, SDK-side audio failures are rare — but if the
    // source does die (device unplugged mid-session, recorder failure), name the cause instead
    // of a generic retry nudge. Error names per @soniox/client's typed audio errors.
    if (error?.name === 'AudioPermissionError') {
      this.showDictationError('permission');
    } else if (error?.name === 'AudioDeviceError') {
      this.showDictationError('nodevice');
    } else {
      this.flashBlocked(this.label('unavailable_retry'));
    }
  }

  // Why the mic can't be used at all in this context, or null when it can. The capabilities
  // probed are the ones the dictation pipeline actually uses: getUserMedia (capture),
  // MediaRecorder (encoding — what warmStreamSource runs on), WebSocket (streaming). Order
  // matters: an insecure (non-https, non-localhost) origin strips navigator.mediaDevices, which
  // would ALSO fail the capability check — so the connection must be checked first, or a current
  // browser on a plain-HTTP LAN address gets mislabeled "unsupported" and told to update.
  // isSecureContext === false only (not merely falsy): a browser too old to define it should
  // fall through to the genuine capability check, not be blamed on the connection.
  detectUnavailableReason() {
    if (window.isSecureContext === false) return 'insecure';
    if (!micSupported() || typeof MediaRecorder === 'undefined' || !('WebSocket' in window)) return 'unsupported';
    return null;
  }

  // A concrete, specific detail line under the friendly message — the actual reason, not a
  // guess. For an insecure origin that's the address in the bar; for a genuinely unsupported
  // browser it's the specific capabilities that are missing, so we never claim "update your
  // browser" without saying what's actually absent. Diagnostic text, kept in English.
  unavailableDetail(reason) {
    if (reason === 'insecure') {
      return `This page is loaded over an insecure connection (${window.location.origin}). Microphones are only available over https, or on localhost during development.`;
    }
    if (reason === 'unsupported') {
      const missing = [];
      if (!navigator.mediaDevices?.getUserMedia) missing.push('microphone access (getUserMedia)');
      if (typeof MediaRecorder === 'undefined') missing.push('audio recording (MediaRecorder)');
      if (!('WebSocket' in window)) missing.push('live connection (WebSocket)');
      return missing.length
        ? `This browser is missing: ${missing.join(', ')}.`
        : 'This browser lacks an audio feature voice dictation needs. The latest Chrome, Edge, or Safari will work.';
    }
    return null;
  }

  // Present-but-muted mic: discoverable (not vanished) and self-explaining on click.
  markUnavailable() {
    this.buttonTarget.classList.add('voice-dictation__button--unavailable');
    this.iconTarget.className = 'bi bi-mic-mute';
    const hint = this.label('unavailable_hint');
    this.buttonTarget.title = hint;
    this.buttonTarget.setAttribute('aria-label', hint);
  }

  explainUnavailable() {
    this.showDictationError(this.unavailableReason);
  }

  // Map a getUserMedia rejection to a specific cause so the message is accurate instead of a
  // blanket "blocked". Names per the MediaDevices spec; an unknown name is treated as a denied
  // or dismissed permission prompt, the most common real cause.
  classifyMediaError(error) {
    switch (error?.name) {
      case 'NotFoundError':
      case 'DevicesNotFoundError':
        return 'nodevice';
      case 'NotReadableError':
      case 'TrackStartError':
      case 'AbortError':
        return 'inuse';
      case 'NotAllowedError':
      case 'SecurityError':
      default:
        return 'permission';
    }
  }

  // The single surface for "here's why dictation can't run": a user-facing modal (visible at
  // any scroll position) plus a support-facing console line naming the cause and the two facts
  // that decide mic availability — the answer to a "it doesn't work on my machine" report.
  showDictationError(reason) {
    const detail = this.unavailableDetail(reason);
    // eslint-disable-next-line no-console
    console.warn(
      `Voice dictation unavailable (${reason}) — origin=${window.location.origin}, secureContext=${window.isSecureContext}, mediaDevices=${!!navigator
        .mediaDevices
        ?.getUserMedia}, mediaRecorder=${typeof MediaRecorder !== 'undefined'}${detail ? ` — ${detail}` : ''}`
    );
    showDictationDialog(
      this.label('unavailable_heading'),
      this.label(`error_${reason}`),
      this.label('dismiss'),
      detail
    );
  }

  // --- UI states ---

  showConnecting() {
    this.buttonTarget.classList.add('voice-dictation__button--connecting');
    this.buttonTarget.setAttribute('aria-busy', 'true');
    this.buttonTarget.title = this.label('connecting');
  }

  showRecording() {
    if (this.starting) {
      this.debugLog('RED — talk now');
      this.startSessionLimits();
    }
    this.starting = false;
    clearTimeout(this.startTimeout);
    this.startTimeout = null;
    this.buttonTarget.classList.remove('voice-dictation__button--connecting');
    this.buttonTarget.classList.add('voice-dictation__button--recording');
    this.buttonTarget.removeAttribute('aria-busy');
    this.buttonTarget.setAttribute('aria-pressed', 'true');
    this.buttonTarget.title = this.label('stop');
    this.iconTarget.className = 'bi bi-mic-fill';
  }

  resetUi() {
    this.buttonTarget.classList.remove(
      'voice-dictation__button--connecting',
      'voice-dictation__button--recording',
      'voice-dictation__button--blocked'
    );
    this.buttonTarget.removeAttribute('aria-busy');
    this.buttonTarget.setAttribute('aria-pressed', 'false');
    this.buttonTarget.title = this.label('dictate');
    this.iconTarget.className = 'bi bi-mic';
  }

  flashBlocked(title) {
    this.buttonTarget.classList.add('voice-dictation__button--blocked');
    this.buttonTarget.title = title;
    this.iconTarget.className = 'bi bi-mic-mute';
    this.blockedTimer = setTimeout(() => this.resetUi(), 2500);
  }
}
